/**
 * Project Log Work — port of the original updateModal(): the project's own update and statistics for one day
 * (one per project per date). `ProjectPickerModal` is the first step when no project is known yet.
 */
import { useRef, useState } from 'react';
import type { MetricKind, Project, ProjectMetric, ProjectUpdate, ProjectUpdateInput } from '@/types/models';
import { byDateDesc, today } from '@/domain/format';
import { kindOf } from '@/domain/metrics';
import { LIMITS, validateProjectUpdate, type FieldErrors } from '@/domain/validation';
import { useAction, useDataset } from '@/hooks/data';
import { useToast } from '@/app/AppContext';
import { ErrorState, Field, KindTag, LoadingState, ModalFrame } from '@/ui/kit';

const DUPLICATE = 'A project log for this date already exists. Edit it instead.';
const PLACEHOLDER: Record<MetricKind, string> = { asof: 'Count now', daily: 'Today only', todate: 'Total so far' };

/* ---------- step 1: pick the project ---------- */

export function ProjectPickerModal({ onClose, onContinue }: { onClose: () => void; onContinue: (projectId: string) => void }) {
  const ds = useDataset();
  const [projectId, setProjectId] = useState('');
  const [err, setErr] = useState('');
  const title = 'Project Log Work';
  if (ds.error) return <ModalFrame title={title} onClose={onClose}><ErrorState error={ds.error} onRetry={ds.refetch} /></ModalFrame>;
  if (!ds.data) return <ModalFrame title={title} onClose={onClose}><LoadingState /></ModalFrame>;
  const submit = () => {
    const e = projectId ? '' : 'Project is required.';
    setErr(e);
    if (!e) onContinue(projectId);
  };
  return (
    <form onSubmit={(e) => (e.preventDefault(), submit())} noValidate>
      <ModalFrame
        title={title}
        onClose={onClose}
        footer={
          <>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn primary">
              Continue
            </button>
          </>
        }
      >
        <Field label="Project" required error={err}>
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} aria-label="Project">
            <option value="">Select Project</option>
            {ds.data.projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        <p className="hint">Project Log Work records the project's own update and statistics for a day — no member, no hours. Use Employee Log Work for who did what.</p>
      </ModalFrame>
    </form>
  );
}

/* ---------- step 2: add / edit ---------- */

export function ProjectLogModal({ id, projectId, onClose }: { id?: string; projectId?: string; onClose: () => void }) {
  const ds = useDataset();
  const existing = id ? ds.data?.updates.find((u) => u.id === id) : undefined;
  const p = ds.data?.projects.find((x) => x.id === (existing ? existing.projectId : projectId));
  const title = `${id ? 'Edit' : 'Add'} Project Log Work${p ? ` — ${p.name}` : ''}`;
  if (ds.error) return <ModalFrame title={title} onClose={onClose}><ErrorState error={ds.error} onRetry={ds.refetch} /></ModalFrame>;
  if (!ds.data) return <ModalFrame title={title} onClose={onClose}><LoadingState /></ModalFrame>;
  if ((id && !existing) || !p) return <ModalFrame title={title} onClose={onClose}><div className="empty">No records found.</div></ModalFrame>;
  return <ProjectLogForm key={id || p.id} project={p} existing={existing} updates={ds.data.updates} title={title} onClose={onClose} />;
}

interface Row {
  key: number;
  label: string;
  value: string;
  /**
   * Rows filled in when the form opens show the metric's type tag and a matching placeholder,
   * taken from the name they opened with (as the original did). Rows added with "+ Add metric" have none.
   */
  prefilled?: { label: string; kind: MetricKind | '' };
}

/** Edit → the log's own metrics; new → the configured metrics, else the names used in the latest Project Log, with empty values. */
function initialMetrics(p: Project, existing: ProjectUpdate | undefined, updates: ProjectUpdate[]): ProjectMetric[] {
  if (existing) return existing.metrics;
  if (p.metricKeys.length) return p.metricKeys.map((label) => ({ label, value: '' }));
  const latest = updates.filter((u) => u.projectId === p.id).sort(byDateDesc)[0];
  return (latest?.metrics || []).map((m) => ({ label: m.label, value: '' }));
}

function ProjectLogForm({ project: p, existing: u, updates, title, onClose }: { project: Project; existing?: ProjectUpdate; updates: ProjectUpdate[]; title: string; onClose: () => void }) {
  const toast = useToast();
  const seq = useRef(0);
  const [date, setDate] = useState(u ? u.date : today());
  const [note, setNote] = useState(u ? u.note : '');
  const [rows, setRows] = useState<Row[]>(() => initialMetrics(p, u, updates).map((m) => ({ key: ++seq.current, label: m.label, value: m.value, prefilled: { label: m.label, kind: kindOf(p, m.label) } })));
  const [err, setErr] = useState<FieldErrors>({});
  const save = useAction(
    (s, rec: ProjectUpdateInput) => (u ? s.projectUpdates.update(u.id, { date: rec.date, note: rec.note, metrics: rec.metrics }) : s.projectUpdates.create(rec)),
    u ? 'Project log updated successfully.' : 'Project log saved successfully.',
  );
  const isDuplicate = (d: string) => updates.some((x) => x.projectId === p.id && x.date === d && x.id !== (u ? u.id : ''));
  const setRow = (key: number, patch: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, { key: ++seq.current, label: '', value: '' }]);
  const submit = async () => {
    const e = validateProjectUpdate({ date, projectId: p.id });
    setErr(e);
    if (Object.keys(e).length) return;
    const metrics = rows.map((r) => ({ label: r.label.trim(), value: r.value.trim() })).filter((m) => m.label && m.value !== '');
    if (isDuplicate(date)) {
      setErr({ date: DUPLICATE });
      toast(DUPLICATE, true);
      return;
    }
    const ok = await save.run({ date, projectId: p.id, note: note.trim(), metrics });
    if (ok) onClose();
  };
  return (
    <form onSubmit={(e) => (e.preventDefault(), void submit())} noValidate>
      <ModalFrame
        title={title}
        onClose={onClose}
        footer={
          <>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={save.pending}>
              {u ? 'Update' : 'Save'}
            </button>
          </>
        }
      >
        <Field label="Date" required error={err.date} style={{ maxWidth: 260 }}>
          <input
            type="date"
            name="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              setErr({ ...err, date: isDuplicate(e.target.value) ? DUPLICATE : '' });
            }}
          />
        </Field>
        <Field label="Project update (goes into the daily report as-is)">
          <textarea name="note" maxLength={LIMITS.note} placeholder="What happened on this project today — status, progress, blockers" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <label>Statistics / analytics for the day</label>
        <div>
          {rows.map((r) => {
            const label = (
              <input placeholder="Metric name" value={r.label} maxLength={LIMITS.metricLabel} aria-label="Metric name" autoFocus={!r.prefilled} onChange={(e) => setRow(r.key, { label: e.target.value })} />
            );
            const kind = r.prefilled?.label ? r.prefilled.kind : '';
            return (
              <div className="kv-row" key={r.key}>
                {r.prefilled ? (
                  <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                    {label}
                    {r.prefilled.label ? <KindTag kind={kind} /> : null}
                  </div>
                ) : (
                  label
                )}
                <input placeholder={kind ? PLACEHOLDER[kind] : 'Value'} value={r.value} maxLength={LIMITS.metricValue} aria-label="Value" onChange={(e) => setRow(r.key, { value: e.target.value })} />
                <button type="button" className="icon-btn danger" data-tip="Remove" aria-label="Remove" onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}>
                  ✕
                </button>
              </div>
            );
          })}
        </div>
        <button type="button" className="btn sm" onClick={addRow}>
          + Add metric
        </button>
      </ModalFrame>
    </form>
  );
}
